import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const CookiePolicyPage = () => (
  <main className="pt-20" data-testid="cookie-policy-page">
    <PageSEO {...SEO.cookiePolicy} />
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <h1 className="font-heading text-5xl text-[#0D1527] mb-8">COOKIE POLICY</h1>
        <div className="prose prose-invert max-w-none space-y-6 text-[#4A5568]">
          <p className="text-[#64708A] text-sm">Ultimo aggiornamento: Marzo 2026</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">1. Cosa Sono i Cookie</h2>
          <p>I cookie sono piccoli file di testo che i siti web visitati inviano al browser dell'utente, dove vengono memorizzati per essere poi ritrasmessi agli stessi siti alla visita successiva. Sono utilizzati per diverse finalita, hanno caratteristiche diverse e possono essere utilizzati sia dal titolare del sito che si sta visitando, sia da terze parti.</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">2. Titolare del Trattamento</h2>
          <p><strong className="text-[#0D1527]">SC Dottori Commercialisti SRL STP</strong>, con sede legale in Via Ponte Marino 41, 48121 Ravenna (RA), P.IVA IT02677200392, email: assistenza@casalisilvia.com</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">3. Tipologie di Cookie Utilizzati</h2>
          
          <h3 className="font-heading text-xl text-[#0D1527] mt-6">Cookie Tecnici (necessari)</h3>
          <p>Questi cookie sono essenziali per il corretto funzionamento del sito. Includono cookie di sessione e cookie funzionali che consentono la navigazione e l'utilizzo delle funzionalita del sito. Non richiedono il consenso dell'utente.</p>
          
          <h3 className="font-heading text-xl text-[#0D1527] mt-6">Cookie di Funzionalita</h3>
          <p>Utilizzati per memorizzare le preferenze dell'utente (ad esempio: autenticazione all'area admin, preferenze di visualizzazione). Migliorano l'esperienza di navigazione senza raccogliere dati identificativi.</p>

          <h3 className="font-heading text-xl text-[#0D1527] mt-6">Cookie di Misurazione Interna (prima parte)</h3>
          <p>Per capire quante persone visitano il sito e da dove arrivano, utilizziamo cookie tecnici di prima parte impostati direttamente dal nostro dominio:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-[#0D1527]">sc_origin</strong> (durata 30 giorni): conserva l'origine della prima visita, cioe i parametri di campagna presenti nell'indirizzo (utm_source, utm_medium, utm_campaign, gclid) oppure il sito di provenienza (ad esempio un motore di ricerca o un social).</li>
            <li><strong className="text-[#0D1527]">sc_vid</strong> (durata 30 giorni): identificativo casuale e anonimo, usato solo per contare i visitatori unici e le pagine viste.</li>
            <li><strong className="text-[#0D1527]">sc_attr</strong> (durata 90 giorni): conserva gli identificativi di click delle campagne pubblicitarie, per attribuire correttamente richieste e acquisti.</li>
          </ul>
          <p>Questi dati restano nei nostri sistemi, non vengono condivisi con terze parti per finalita proprie e <strong className="text-[#0D1527]">non comportano la memorizzazione dell'indirizzo IP</strong>. Le statistiche sono aggregate e servono a misurare l'andamento del sito e l'efficacia delle campagne.</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">4. Cookie di Terze Parti</h2>
          <p>Il sito potrebbe utilizzare servizi di terze parti che installano cookie propri:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-[#0D1527]">Unsplash:</strong> per il caricamento delle immagini degli articoli del blog</li>
            <li><strong className="text-[#0D1527]">Font Google:</strong> per il caricamento dei font utilizzati nel sito</li>
          </ul>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">5. Gestione dei Cookie</h2>
          <p>L'utente puo gestire le preferenze sui cookie direttamente tramite le impostazioni del proprio browser. Di seguito i link alle guide per i browser principali:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-[#0D1527]">Chrome:</strong> Impostazioni &gt; Privacy e sicurezza &gt; Cookie</li>
            <li><strong className="text-[#0D1527]">Firefox:</strong> Opzioni &gt; Privacy e sicurezza &gt; Cookie</li>
            <li><strong className="text-[#0D1527]">Safari:</strong> Preferenze &gt; Privacy &gt; Cookie</li>
            <li><strong className="text-[#0D1527]">Edge:</strong> Impostazioni &gt; Cookie e autorizzazioni sito</li>
          </ul>
          <p>La disattivazione dei cookie tecnici potrebbe compromettere il corretto funzionamento del sito.</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">6. Diritti dell'Utente</h2>
          <p>Per esercitare i diritti previsti dal GDPR (accesso, rettifica, cancellazione, opposizione), contattare: <strong className="text-gold">assistenza@casalisilvia.com</strong></p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">7. Aggiornamenti</h2>
          <p>La presente Cookie Policy puo essere aggiornata periodicamente. Si consiglia di consultare questa pagina regolarmente per essere informati su eventuali modifiche.</p>
          
          <h2 className="font-heading text-2xl text-[#0D1527] mt-8">8. Contatti</h2>
          <p><strong className="text-[#0D1527]">SC Dottori Commercialisti SRL STP</strong><br />Via Ponte Marino 41, 48121 Ravenna (RA)<br />Email: assistenza@casalisilvia.com<br />P.IVA: IT02677200392</p>
        </div>
      </div>
    </section>
  </main>
);

// ============ STRUMENTI (Digital Products) ============


export { CookiePolicyPage };
